import React, { useState, useEffect } from 'react';
import { Users, Eye, Target, TrendingUp, Filter } from 'lucide-react';

const AudienceCreative = ({ dashboardData, filters, onFilterChange }) => {
  const [selectedMetric, setSelectedMetric] = useState('ctr');
  const [selectedAudienceType, setSelectedAudienceType] = useState('demographics');
  const [selectedCreative, setSelectedCreative] = useState('all');

  const audienceTypes = [
    {
      key: 'demographics',
      label: 'Demographics',
      icon: <Users className="h-5 w-5" />,
      options: ['age_18_24', 'age_25_34', 'age_35_44', 'age_45_54', 'age_55_plus', 'male', 'female']
    },
    {
      key: 'interests',
      label: 'Interest Categories',
      icon: <Target className="h-5 w-5" />,
      options: ['fashion', 'technology', 'fitness', 'food', 'travel', 'business', 'entertainment']
    },
    {
      key: 'custom',
      label: 'Custom Audiences',
      icon: <Eye className="h-5 w-5" />,
      options: ['lookalike_1', 'lookalike_2', 'website_visitors', 'email_subscribers', 'past_purchasers']
    }
  ];

  const metrics = [
    { key: 'ctr', label: 'CTR', icon: '📊', format: 'percentage', color: 'green' },
    { key: 'cvr', label: 'CVR', icon: '📉', format: 'percentage', color: 'purple' },
    { key: 'roas', label: 'ROAS', icon: '🚀', format: 'multiplier', color: 'blue' },
    { key: 'cpa', label: 'CPA', icon: '💸', format: 'currency', color: 'red' }
  ];

  // Mock data for demonstration - in real app this would come from API
  const mockData = {
    demographics: {
      age_18_24: { ctr: 0.028, cvr: 0.085, roas: 3.8, cpa: 12.50 },
      age_25_34: { ctr: 0.025, cvr: 0.078, roas: 3.4, cpa: 14.20 },
      age_35_44: { ctr: 0.022, cvr: 0.072, roas: 3.1, cpa: 15.80 },
      age_45_54: { ctr: 0.019, cvr: 0.065, roas: 2.8, cpa: 17.50 },
      age_55_plus: { ctr: 0.016, cvr: 0.058, roas: 2.5, cpa: 19.20 },
      male: { ctr: 0.021, cvr: 0.070, roas: 3.0, cpa: 16.00 },
      female: { ctr: 0.024, cvr: 0.075, roas: 3.3, cpa: 14.80 }
    },
    interests: {
      fashion: { ctr: 0.026, cvr: 0.080, roas: 3.5, cpa: 13.50 },
      technology: { ctr: 0.023, cvr: 0.072, roas: 3.2, cpa: 15.20 },
      fitness: { ctr: 0.025, cvr: 0.078, roas: 3.4, cpa: 14.00 },
      food: { ctr: 0.027, cvr: 0.082, roas: 3.6, cpa: 12.80 },
      travel: { ctr: 0.020, cvr: 0.065, roas: 2.9, cpa: 16.50 },
      business: { ctr: 0.022, cvr: 0.068, roas: 3.0, cpa: 15.80 },
      entertainment: { ctr: 0.024, cvr: 0.075, roas: 3.3, cpa: 14.20 }
    },
    custom: {
      lookalike_1: { ctr: 0.029, cvr: 0.088, roas: 3.9, cpa: 11.80 },
      lookalike_2: { ctr: 0.026, cvr: 0.080, roas: 3.5, cpa: 13.20 },
      website_visitors: { ctr: 0.025, cvr: 0.078, roas: 3.4, cpa: 13.80 },
      email_subscribers: { ctr: 0.027, cvr: 0.082, roas: 3.6, cpa: 12.50 },
      past_purchasers: { ctr: 0.030, cvr: 0.090, roas: 4.0, cpa: 11.20 }
    }
  };

  const creatives = [
    { id: 'creative_1', name: 'Product Demo Video', type: 'video', performance: 'high' },
    { id: 'creative_2', name: 'Testimonial Static', type: 'static', performance: 'medium' },
    { id: 'creative_3', name: 'Lifestyle Carousel', type: 'carousel', performance: 'low' },
    { id: 'creative_4', name: 'UGC Compilation', type: 'video', performance: 'high' },
    { id: 'creative_5', name: 'Feature Comparison', type: 'static', performance: 'medium' }
  ];

  const formatValue = (value, format) => {
    switch (format) {
      case 'percentage':
        return `${(value * 100).toFixed(2)}%`;
      case 'currency':
        return `$${value.toFixed(2)}`;
      case 'multiplier':
        return `${value.toFixed(2)}x`;
      default:
        return value.toFixed(2);
    }
  };

  const getAudienceLabel = (type, value) => {
    switch (type) {
      case 'demographics':
        if (value.startsWith('age_')) {
          const age = value.replace('age_', '').replace('_', '-');
          return `${age} years`;
        }
        return value === 'male' ? 'Male' : 'Female';
      case 'interests':
        return value.charAt(0).toUpperCase() + value.slice(1);
      case 'custom':
        return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      default:
        return value;
    }
  };

  const getMetricColor = (value, metric) => {
    const data = mockData[selectedAudienceType];
    const values = Object.values(data).map(item => item[metric]);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min;
    
    if (range === 0) return 'text-slate-400';
    
    const normalized = (value - min) / range;
    
    if (metric === 'cpa') {
      // Lower CPA is better
      return normalized <= 0.33 ? 'text-green-400' : normalized <= 0.66 ? 'text-yellow-400' : 'text-red-400';
    } else {
      // Higher values are better for other metrics
      return normalized >= 0.67 ? 'text-green-400' : normalized >= 0.33 ? 'text-yellow-400' : 'text-red-400';
    }
  };

  const renderHeatmap = () => {
    const data = mockData[selectedAudienceType];
    const metricData = metrics.find(m => m.key === selectedMetric);
    
    if (!data || !metricData) return <div>No data available</div>;

    const sortedAudiences = Object.entries(data)
      .map(([key, value]) => ({ key, value: value[selectedMetric] }))
      .sort((a, b) => {
        if (selectedMetric === 'cpa') {
          return a.value - b.value; // Lower CPA is better
        } else {
          return b.value - a.value; // Higher values are better
        }
      });

    const maxValue = Math.max(...sortedAudiences.map(d => d.value));
    const minValue = Math.min(...sortedAudiences.map(d => d.value));

    return (
      <div className="space-y-3">
        {sortedAudiences.map((audience) => {
          const percentage = maxValue > 0 ? ((audience.value - minValue) / (maxValue - minValue)) * 100 : 0;
          const isBest = selectedMetric === 'cpa' ? audience.value === minValue : audience.value === maxValue;
          
          return (
            <div key={audience.key} className="flex items-center space-x-4">
              <div className="w-32 text-sm font-medium text-slate-300">
                {getAudienceLabel(selectedAudienceType, audience.key)}
              </div>
              <div className="flex-1">
                <div className="w-full bg-slate-700 rounded-full h-4">
                  <div 
                    className={`h-4 rounded-full transition-all ${
                      isBest ? 'bg-green-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
              <div className={`w-20 text-right text-sm font-semibold ${getMetricColor(audience.value, selectedMetric)}`}>
                {formatValue(audience.value, metricData.format)}
              </div>
              {isBest && (
                <span className="px-2 py-1 text-xs bg-green-600/50 text-green-200 rounded-full">
                  Best
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCrossTab = () => {
    const data = mockData[selectedAudienceType];
    const metricData = metrics.find(m => m.key === selectedMetric);
    
    if (!data || !metricData) return <div>No data available</div>;

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left p-3 text-slate-300 font-medium">Audience</th>
              {creatives.map(creative => (
                <th key={creative.id} className="text-center p-3 text-slate-300 font-medium">
                  <div className="flex flex-col items-center space-y-1">
                    <span className="text-xs">{creative.name}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      creative.performance === 'high' ? 'bg-green-600/50 text-green-200' :
                      creative.performance === 'medium' ? 'bg-yellow-600/50 text-yellow-200' :
                      'bg-red-600/50 text-red-200'
                    }`}>
                      {creative.performance}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(data).map(([audienceKey, audienceData]) => (
              <tr key={audienceKey} className="border-b border-slate-800 hover:bg-slate-800/30">
                <td className="p-3 text-slate-300 font-medium">
                  {getAudienceLabel(selectedAudienceType, audienceKey)}
                </td>
                {creatives.map(creative => {
                  // Simulate creative-specific performance variations
                  const baseValue = audienceData[selectedMetric];
                  const variation = (Math.random() - 0.5) * 0.3; // ±15% variation
                  const value = baseValue * (1 + variation);
                  
                  return (
                    <td key={creative.id} className="text-center p-3">
                      <span className={`text-sm font-medium ${getMetricColor(value, selectedMetric)}`}>
                        {formatValue(value, metricData.format)}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTopPerformers = () => {
    const data = mockData[selectedAudienceType];
    const metricData = metrics.find(m => m.key === selectedMetric);
    
    if (!data || !metricData) return <div>No data available</div>;

    const sortedAudiences = Object.entries(data)
      .map(([key, value]) => ({ key, value: value[selectedMetric] }))
      .sort((a, b) => {
        if (selectedMetric === 'cpa') {
          return a.value - b.value; // Lower CPA is better
        } else {
          return b.value - a.value; // Higher values are better
        }
      });

    const top3 = sortedAudiences.slice(0, 3);
    const bottom3 = sortedAudiences.slice(-3).reverse();

    return (
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h4 className="text-lg font-semibold text-white mb-3">Top 3 Performers</h4>
          <div className="space-y-2">
            {top3.map((audience, index) => (
              <div key={audience.key} className="flex items-center justify-between p-3 bg-green-600/10 border border-green-600/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="text-green-400 font-bold">#{index + 1}</span>
                  <span className="text-slate-300">{getAudienceLabel(selectedAudienceType, audience.key)}</span>
                </div>
                <span className="text-green-400 font-semibold">
                  {formatValue(audience.value, metricData.format)}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h4 className="text-lg font-semibold text-white mb-3">Optimization Targets</h4>
          <div className="space-y-2">
            {bottom3.map((audience, index) => (
              <div key={audience.key} className="flex items-center justify-between p-3 bg-red-600/10 border border-red-600/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="text-red-400 font-bold">#{bottom3.length - index}</span>
                  <span className="text-slate-300">{getAudienceLabel(selectedAudienceType, audience.key)}</span>
                </div>
                <span className="text-red-400 font-semibold">
                  {formatValue(audience.value, metricData.format)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Audience × Creative Analysis</h2>
        <p className="text-slate-400">Identify which audiences respond best to which creatives</p>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-3 gap-4">
        {/* Audience Type Selection */}
        <div className="bg-slate-800/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Audience Type</h4>
          <div className="space-y-2">
            {audienceTypes.map((type) => (
              <button
                key={type.key}
                onClick={() => setSelectedAudienceType(type.key)}
                className={`w-full flex items-center space-x-2 p-2 rounded-lg border transition-all ${
                  selectedAudienceType === type.key
                    ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                    : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                }`}
              >
                {type.icon}
                <span className="text-sm font-medium">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Metric Selection */}
        <div className="bg-slate-800/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Performance Metric</h4>
          <div className="grid grid-cols-2 gap-2">
            {metrics.map((metric) => (
              <button
                key={metric.key}
                onClick={() => setSelectedMetric(metric.key)}
                className={`flex flex-col items-center space-y-1 p-2 rounded-lg border transition-all ${
                  selectedMetric === metric.key
                    ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                    : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                }`}
              >
                <span className="text-lg">{metric.icon}</span>
                <span className="text-xs font-medium">{metric.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Creative Selection */}
        <div className="bg-slate-800/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Creative Focus</h4>
          <div className="space-y-2">
            <button
              onClick={() => setSelectedCreative('all')}
              className={`w-full p-2 rounded-lg border transition-all ${
                selectedCreative === 'all'
                  ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                  : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
              }`}
            >
              All Creatives
            </button>
            {creatives.map((creative) => (
              <button
                key={creative.id}
                onClick={() => setSelectedCreative(creative.id)}
                className={`w-full p-2 rounded-lg border transition-all ${
                  selectedCreative === creative.id
                    ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                    : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                }`}
              >
                <div className="text-left">
                  <div className="text-sm font-medium">{creative.name}</div>
                  <div className="text-xs text-slate-400">{creative.type}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Top Performers vs Optimization Targets */}
      {renderTopPerformers()}

      {/* Heatmap */}
      <div className="bg-slate-800/30 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-white mb-4">
          {audienceTypes.find(t => t.key === selectedAudienceType)?.label} Performance by {metrics.find(m => m.key === selectedMetric)?.label}
        </h4>
        {renderHeatmap()}
      </div>

      {/* Cross-tab Analysis */}
      <div className="bg-slate-800/30 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-white mb-4">Creative × Audience Performance Matrix</h4>
        {renderCrossTab()}
      </div>

      {/* Insights */}
      <div className="bg-slate-800/30 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-white mb-4">Key Insights</h4>
        <div className="space-y-3 text-sm text-slate-300">
          <div className="flex items-start space-x-2">
            <span className="text-green-400 mt-1">•</span>
            <span>
              <strong>{getAudienceLabel(selectedAudienceType, Object.keys(mockData[selectedAudienceType]).find(key => {
                const data = mockData[selectedAudienceType][key];
                if (selectedMetric === 'cpa') {
                  return data[selectedMetric] === Math.min(...Object.values(mockData[selectedAudienceType]).map(item => item[selectedMetric]));
                } else {
                  return data[selectedMetric] === Math.max(...Object.values(mockData[selectedAudienceType]).map(item => item[selectedMetric]));
                }
              }))}</strong> shows the best {selectedMetric.toUpperCase()} performance
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-blue-400 mt-1">•</span>
            <span>
              Consider creating more targeted content for underperforming audience segments
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-purple-400 mt-1">•</span>
            <span>
              Test different creative formats specifically for your top-performing audiences
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudienceCreative;
